<!-- NoteActionsButton.vue -->
<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";

const emit = defineEmits([
  "add-detached",
  "bulk-add",
  "bulk-edit",
  "bulk-remove",
]);

const props = defineProps({
  hasSelectedExcerpts: { type: Boolean, default: false },
  hasSelectedNotes: { type: Boolean, default: false },
  // Criador da atividade atual — necessário para a regra "somente conteúdo do titular".
  activityCreatedByUserId: { type: Number, default: null },
});

// Combina grid do vínculo + opção "Somente conteúdo do titular".
const { canCreateNoteInActivity, canManageNotesInActivity } =
  useMemberRestrictions();

const memberCanCreateNote = computed(() =>
  canCreateNoteInActivity(props.activityCreatedByUserId)
);
// Edição/exclusão em lote: gate no nível da atividade (autoria é validada por
// nota no backend).
const memberCanEditNote = computed(() =>
  canManageNotesInActivity("edit", props.activityCreatedByUserId)
);
const memberCanDeleteNote = computed(() =>
  canManageNotesInActivity("delete", props.activityCreatedByUserId)
);

// Motivo exibido no tooltip das ações que o vínculo bloqueia.
const blockedByMembershipHint =
  "Você só pode anotar em atividades preparadas pelo titular ou pelo círculo dele.";

const isDropdownOpen = ref(false);
const dropdownRef = ref(null);

// ── Toast ─────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showInfoToast(message) {
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ─────────────────────────────────────────────────────────────────────────────

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function handleClickOutside(event) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isDropdownOpen.value = false;
  }
}

function handleAddDetached() {
  isDropdownOpen.value = false;
  showInfoToast(
    "Selecione com o mouse uma palavra ou trecho no texto para criar uma nota vinculada."
  );
}

function handleBulkAdd() {
  if (!props.hasSelectedExcerpts) return;
  isDropdownOpen.value = false;
  window.dispatchEvent(new CustomEvent("note-action-clicked"));
  emit("bulk-add");
}

function handleBulkEdit() {
  if (!props.hasSelectedNotes) return;
  isDropdownOpen.value = false;
  window.dispatchEvent(new CustomEvent("note-action-clicked"));
  emit("bulk-edit");
}

function handleBulkRemove() {
  if (!props.hasSelectedNotes) return;
  isDropdownOpen.value = false;
  window.dispatchEvent(new CustomEvent("note-action-clicked"));
  emit("bulk-remove");
}

onMounted(() => document.addEventListener("click", handleClickOutside));
onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      type="button"
      @click.stop="toggleDropdown"
      class="btn btn-batch w-full"
    >
      <i class="fas fa-bolt mr-1"></i>
      Ações
      <svg
        class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
        :class="{ open: isDropdownOpen }"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          clip-rule="evenodd"
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
        />
      </svg>
    </button>

    <div v-if="isDropdownOpen" class="dropdown-menu">
      <div class="py-1">
        <!-- Bloqueado pelo vínculo: a ação continua listada, esmaecida e inerte
             (mesmo tratamento de "nada selecionado"), com o motivo no tooltip. -->
        <button
          type="button"
          class="dropdown-item"
          :class="{ 'dropdown-item--disabled': !memberCanCreateNote }"
          :disabled="!memberCanCreateNote"
          :title="memberCanCreateNote ? null : blockedByMembershipHint"
          @click.prevent.stop="handleAddDetached"
        >
          <i class="fas fa-link"></i>
          <span>Criar nota vinculada</span>
        </button>

        <button
          type="button"
          class="dropdown-item"
          :class="{
 'dropdown-item--disabled': !hasSelectedExcerpts || !memberCanCreateNote,
          }"
          :disabled="!hasSelectedExcerpts || !memberCanCreateNote"
          :title="memberCanCreateNote ? null : blockedByMembershipHint"
          @click.prevent.stop="handleBulkAdd"
        >
          <i class="fas fa-plus-circle"></i>
          <span>Criar notas em lote</span>
        </button>

        <button
          type="button"
          class="dropdown-item"
          :class="{
 'dropdown-item--disabled': !hasSelectedNotes || !memberCanEditNote,
          }"
          :disabled="!hasSelectedNotes || !memberCanEditNote"
          :title="memberCanEditNote ? null : blockedByMembershipHint"
          @click.prevent.stop="handleBulkEdit"
        >
          <i class="fas fa-edit"></i>
          <span>Editar notas selecionadas</span>
        </button>

        <div class="border-t border-gray-200 my-1"></div>

        <button
          type="button"
          class="dropdown-item danger-item"
          :class="{
 'dropdown-item--disabled': !hasSelectedNotes || !memberCanDeleteNote,
          }"
          :disabled="!hasSelectedNotes || !memberCanDeleteNote"
          :title="memberCanDeleteNote ? null : blockedByMembershipHint"
          @click.prevent.stop="handleBulkRemove"
        >
          <i class="fas fa-minus-circle"></i>
          <span>Apagar notas selecionadas</span>
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div class="toast toast-info pointer-events-auto">
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                d="M10 9v5M10 6.5v1"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">Como fazer</p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.dropdown-item {
  width: 100%;
}

.dropdown-item--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dropdown-item--disabled:hover {
  background-color: transparent;
}

.toast-info {
  background-color: #e0f2fe;
  border-left: 6px solid #0ea5e9;
  color: #0c4a6e;
}

.toast-info .toast-icon-circle {
  fill: #e0f2fe;
  stroke: #0ea5e9;
}
</style>